import { useEffect, useState, useRef } from 'react'
import { PaymentSDKInstance } from '../../types/payment-sdk'
import { LoadingSpinner } from '../loading-spinner'
import { ErrorDisplay } from '../error-display'
import { ApiError } from '@/lib/api/types'

const SDK_URL = import.meta.env.VITE_SDK_URL ?? 'https://ohui.sandbox.getfanfair.com/sdk/sdk.umd.js'

interface PaymentSDKContainerProps {
  sessionId: string
  environment?: 'sandbox' | 'production'
}

export const PaymentSDKContainer = ({ sessionId, environment = 'sandbox' }: PaymentSDKContainerProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const sdkInstanceRef = useRef<PaymentSDKInstance | null>(null)
  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (!sessionId || isInitializedRef.current) {
      console.log('Skipping SDK init - already initialized:', isInitializedRef.current)
      return
    }

    console.log('🚀 Starting SDK initialization for sessionId:', sessionId)
    isInitializedRef.current = true // Set immediately to prevent double init

    const loadAndOpenSDK = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Ensure container exists
        const container = document.getElementById('payment-container')
        if (!container) {
          throw new Error('Payment container not found in DOM')
        }

        // Load SDK and initialize
        const initSDK = (): Promise<PaymentSDKInstance> =>
          new Promise((resolve, reject) => {
            const SDK = (window as any).FanFairPaymentSDK

            const createInstance = (SDKClass: any) => {
              console.log('📦 Creating SDK instance')
              const instance = new SDKClass({
                environment,
                containerId: 'payment-container',
                onSuccess: (result: any) => {
                  console.log('✅ Payment successful:', result)
                  setIsLoading(false)
                  window.open(`/success?data=${encodeURIComponent(JSON.stringify(result))}`, '_blank')
                },
                onError: (error: any) => {
                  console.error('❌ Payment failed:', error)
                  setError(error?.message || 'Payment failed')
                  setIsLoading(false)
                },
                onClose: () => {
                  console.log('🚪 Payment closed by user')
                  setIsLoading(false)
                  const container = document.getElementById('payment-container')
                  if (container) container.style.display = 'none'
                },
              })
              console.log('⚙️ Calling instance.init()')
              instance.init()
              return instance
            }

            if (SDK) {
              console.log('✓ SDK already loaded')
              resolve(createInstance(SDK))
            } else {
              console.log('⏳ Loading SDK script...')
              const script = document.createElement('script')
              script.src = SDK_URL
              script.onload = () => {
                const SDK = (window as any).FanFairPaymentSDK
                if (!SDK) {
                  reject(new Error('SDK not found'))
                  return
                }
                console.log('✓ SDK script loaded')
                resolve(createInstance(SDK))
              }
              script.onerror = () => reject(new Error('Failed to load SDK'))
              document.head.appendChild(script)
            }
          })

        const sdkInstance = await initSDK()
        sdkInstanceRef.current = sdkInstance

        // Show container and open SDK
        container.style.display = 'block'
        console.log('🎬 Opening SDK with sessionId')
        await (sdkInstance as any).open(sessionId)
        console.log('✓ SDK opened successfully')
        setIsLoading(false)
      } catch (err) {
        console.error('💥 SDK error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load payment')
        setIsLoading(false)
        isInitializedRef.current = false // Reset on error
      }
    }

    // Small delay to ensure DOM is ready
    setTimeout(loadAndOpenSDK, 100)

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up SDK')
      if (sdkInstanceRef.current?.close) {
        try {
          sdkInstanceRef.current.close()
        } catch (err) {
          console.error('Error closing SDK:', err)
        }
      }
      const container = document.getElementById('payment-container')
      if (container) container.style.display = 'none'
      sdkInstanceRef.current = null
      // Don't reset isInitializedRef here - keep it true to prevent re-init
    }
  }, [sessionId, environment])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Loading payment form...</p>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorDisplay
        error={
          {
            status: 500,
            message: error,
            code: 'PAYMENT_ERROR',
          } as ApiError
        }
      />
    )
  }

  return null
}
